import {
  append,
  converge,
  curry,
  identity,
  isEmpty,
  lensPath,
  lensProp,
  over,
  pipe,
  remove,
  set,
  update,
  view,
  when,
  mergeRight,
  none,
  equals,
  pick,
  pickBy,
  propEq,
  find,
  where,
  both,
  not,
  test,
} from "ramda";
import * as fileDetailsActions from "file-details/fileDetailsActions";
import * as basicDetailsActions from "basic-details/basicDetailsActions";
import * as submissionActions from "submission/submissionActions";
import {
  validateContributor,
  validateContributorField,
  validateDetails,
  validateDetailsField,
} from "file-details/fileDetailsValidation";
import {
  completeStep,
  getErrors,
  getField,
  initialStepState,
  setCompleted,
  setErrors,
  setField,
  setFields,
  setLoadingLocalStorage,
} from "common/stepReducerCommon";
import * as applicationReducer from "application/applicationReducer";
import * as submissionReducer from "submission/submissionReducer";
import * as steps from "../common/steps";
import { isSubmitterCreator } from "../intro/introReducer";
import { isBlank, notBlank, notNil } from "../common/general";
import { getAudiences, getSubProjects } from "application/applicationReducer";

const initialDetailsStepState = {
  contributors: [],
  loadingInitData: true,
  videoRatings: [],
  contributorRoles: [],
  licenseTypes: [],
  ageRanges: [],
  contributorDialog: {
    visible: false,
    index: -1,
    fields: {
      role: {},
      type: "PERSON",
    },
    errors: {},
  },
  removeContributorDialog: {
    visible: false,
    track: null,
  },
  removeTrackDialog: {
    visible: false,
    index: -1,
  },
  fields: {
    language: null,
    subProject: null,
    customDropdown: null,
    audience: null,
    audioType: {},
    mediumFormat: {},
    rating: null,
    ageRange: null,
    licenseType: null,
    primaryCategory: {},
    secondaryCategory: {},
    tertiaryCategory: {},
    publicationYear: null,
    publicationDate: null,
  },
};

const initialState = mergeRight(initialStepState, initialDetailsStepState);

const contributorsLens = lensProp("contributors");
const setContributors = set(contributorsLens);
export const getContributors = view(contributorsLens);
export const getContributor = curry(
  (index, state) => getContributors(state)[index] || {},
);

const addContributor = curry((contributor, state) =>
  over(contributorsLens, append(contributor), state),
);

const removeContributor = curry((index, state) =>
  over(contributorsLens, remove(index, 1), state),
);
const updateContributor = curry((index, contributor, state) =>
  over(contributorsLens, update(index, contributor), state),
);

const contributorDialogVisibleLens = lensPath(["contributorDialog", "visible"]);
const setContributorDialogVisible = set(contributorDialogVisibleLens);
export const getContributorDialogVisible = view(contributorDialogVisibleLens);

const contributorDialogIndexLens = lensPath(["contributorDialog", "index"]);
const setContributorDialogIndex = set(contributorDialogIndexLens);
export const getContributorDialogIndex = view(contributorDialogIndexLens);

const videoRatingsLens = lensProp("videoRatings");
const setVideoRatings = set(videoRatingsLens);
export const getVideoRatings = view(videoRatingsLens);

const contributorRolesLens = lensProp("contributorRoles");
const setContributorRoles = set(contributorRolesLens);
export const getContributorRoles = view(contributorRolesLens);

const licenseTypesLens = lensProp("licenseTypes");
const setLicenseTypes = set(licenseTypesLens);
export const getLicenseTypes = view(licenseTypesLens);

const ageRangesLens = lensProp("ageRanges");
const setAgeRanges = set(ageRangesLens);
export const getAgeRanges = view(ageRangesLens);

export const contributorDialogFieldsLens = lensPath([
  "contributorDialog",
  "fields",
]);
export const setContributorDialogFields = set(contributorDialogFieldsLens);
export const getContributorDialogFields = view(contributorDialogFieldsLens);
export const setContributorDialogField = curry((path, value, state) =>
  set(lensPath(["contributorDialog", "fields", ...path]), value, state),
);

export const contributorDialogErrorsLens = lensPath([
  "contributorDialog",
  "errors",
]);
export const setContributorDialogErrors = set(contributorDialogErrorsLens);
export const getContributorDialogErrors = view(contributorDialogErrorsLens);

const audienceChanged = (audience, applicationState, state) => {
  return pipe(setField(["audience"], audience), setCompleted(false))(state);
};

const subProjectChanged = (subProject, applicationState, state) => {
  return pipe(setField(["subProject"], subProject), setCompleted(false))(state);
};

const customDropdownChanged = (customDropdownItem, applicationState, state) => {
  return pipe(
    setField(["customDropdown"], customDropdownItem),
    setCompleted(false),
  )(state);
};

const contributorDialogRoleChanged = (role, state) => {
  return pipe(
    setContributorDialogField(["role"], role),
    setCompleted(false),
  )(state);
};

const videoRatingChanged = (rating, state) => {
  return setField(["rating"], rating, state);
};

const licenseTypeChanged = (licenseType, state) => {
  return setField(["licenseType"], licenseType, state);
};

const ageRangeChanged = (ageRange, state) => {
  return setField(["ageRange"], ageRange, state);
};

const hideContributorDialog = pipe(
  setContributorDialogIndex(initialState.contributorDialog.index),
  setContributorDialogFields(initialState.contributorDialog.fields),
  setContributorDialogErrors(initialState.contributorDialog.errors),
  setContributorDialogVisible(false),
);

const showEditContributorDialog = curry((index, state) =>
  pipe(
    setContributorDialogIndex(index),
    setContributorDialogFields(getContributor(index, state)),
    setContributorDialogVisible(true),
  )(state),
);

const showAddContributorDialog = pipe(
  setContributorDialogIndex(initialState.contributorDialog.index),
  setContributorDialogFields(initialState.contributorDialog.fields),
  setContributorDialogVisible(true),
);

const removeContributorDialogVisibleLens = lensPath([
  "removeContributorDialog",
  "visible",
]);
const setRemoveContributorDialogVisible = set(
  removeContributorDialogVisibleLens,
);
export const getRemoveContributorDialogVisible = view(
  removeContributorDialogVisibleLens,
);

const removeContributorDialogIndexLens = lensPath([
  "removeContributorDialog",
  "index",
]);
const setRemoveContributorDialogIndex = set(removeContributorDialogIndexLens);
export const getRemoveContributorDialogIndex = view(
  removeContributorDialogIndexLens,
);

const hideRemoveContributorDialog = pipe(
  setRemoveContributorDialogVisible(false),
  setRemoveContributorDialogIndex(initialState.removeContributorDialog.index),
);

const showRemoveContributorDialog = (index, state) =>
  pipe(
    setRemoveContributorDialogIndex(index),
    setRemoveContributorDialogVisible(true),
  )(state);

const validateContributorDialog = fieldOptions =>
  converge(setContributorDialogErrors, [
    pipe(getContributorDialogFields, validateContributor(fieldOptions)),
    identity,
  ]);

const validateContributorDialogField = (field, value, fieldOptions, state) =>
  setContributorDialogErrors(
    validateContributorField(
      field,
      value,
      fieldOptions,
      getContributorDialogErrors(state),
    ),
    state,
  );

const isContributorDialogValid = pipe(getContributorDialogErrors, isEmpty);

const saveEditContributor = fieldOptions =>
  pipe(
    validateContributorDialog(fieldOptions),
    when(
      isContributorDialogValid,
      pipe(
        converge(updateContributor, [
          getContributorDialogIndex,
          getContributorDialogFields,
          identity,
        ]),
        hideContributorDialog,
      ),
    ),
  );

const saveNewContributor = fieldOptions =>
  pipe(
    validateContributorDialog(fieldOptions),
    when(
      isContributorDialogValid,
      pipe(
        converge(addContributor, [getContributorDialogFields, identity]),
        hideContributorDialog,
      ),
    ),
  );

const fieldBlurred = (
  workType,
  collectFlags,
  field,
  value,
  fieldOptions,
  state,
) =>
  setErrors(
    validateDetailsField(
      workType,
      collectFlags,
      field,
      value,
      fieldOptions,
      getErrors(state),
    ),
    state,
  );

const removeTrackDialogVisibleLens = lensPath(["removeTrackDialog", "visible"]);
const setRemoveTrackDialogVisible = set(removeTrackDialogVisibleLens);
export const getRemoveTrackDialogVisible = view(removeTrackDialogVisibleLens);

const removeTrackDialogTrackLens = lensPath(["removeTrackDialog", "track"]);
const setRemoveTrackDialogTrack = set(removeTrackDialogTrackLens);
export const getRemoveTrackDialogTrack = view(removeTrackDialogTrackLens);

const hideRemoveTrackDialog = pipe(
  setRemoveTrackDialogVisible(false),
  setRemoveTrackDialogTrack(initialState.removeTrackDialog.track),
);

const showRemoveTrackDialog = (track, state) =>
  pipe(
    setRemoveTrackDialogTrack(track),
    setRemoveTrackDialogVisible(true),
  )(state);

const fieldChanged = (path, value, state) =>
  pipe(setField(path, value), setCompleted(false))(state);

// Contributor should have at least role and either a first or last name
const isWhiteSpace = test(/^\s*$/g);
const notWhiteSpace = val => not(isWhiteSpace(val));
const isValidContributor = where({
  lastName: both(notWhiteSpace, notBlank),
  role: notBlank,
});

const contributorFromBasicDetails = curry(
  (fieldOptions, introState, basicDetailsState, state) => {
    const submitterIsCreator = isSubmitterCreator(introState);

    if (fieldOptions.contributors) {
      const { required, hidden } = fieldOptions.contributors;
      if (!required && hidden) {
        return state;
      }
    }

    const contributor = submitterIsCreator
      ? {
          type: getField(["contributor", "type"], basicDetailsState),
          prefix: getField(["prefix"], basicDetailsState),
          firstName: getField(["firstName"], basicDetailsState),
          middleName: getField(["middleName"], basicDetailsState),
          lastName: getField(["lastName"], basicDetailsState),
          suffix: getField(["suffix"], basicDetailsState),
          role: getField(["role"], basicDetailsState),
          twitterHandle: getField(["twitterHandle"], basicDetailsState),
          website: getField(["website"], basicDetailsState),
        }
      : {
          type: getField(["contributor", "type"], basicDetailsState),
          prefix: getField(["contributor", "prefix"], basicDetailsState),
          firstName: getField(["contributor", "firstName"], basicDetailsState),
          middleName: getField(
            ["contributor", "middleName"],
            basicDetailsState,
          ),
          lastName: getField(["contributor", "lastName"], basicDetailsState),
          suffix: getField(["contributor", "suffix"], basicDetailsState),
          corporationName: getField(
            ["contributor", "corporationName"],
            basicDetailsState,
          ),
          role: getField(["contributor", "role"], basicDetailsState),
          twitterHandle: getField(
            ["contributor", "twitterHandle"],
            basicDetailsState,
          ),
          website: getField(["contributor", "website"], basicDetailsState),
        };

    if (isValidContributor(contributor)) {
      const contributors = getContributors(state);
      if (
        none(
          contrib =>
            equals(
              pick(["firstName", "lastName", "role"], contrib),
              pick(["firstName", "lastName", "role"], contributor),
            ),
          contributors,
        )
      ) {
        return addContributor(contributor, state);
      } else {
        return state;
      }
    } else {
      return state;
    }
  },
);

// ensure that "eng" is even an option in the selected languages for the project
const getDefaultLanguage = projectLanguages => {
  if (
    isBlank(projectLanguages) ||
    find(propEq("code", "eng"), projectLanguages)
  ) {
    return "eng";
  }

  return projectLanguages[0].code;
};

const getFieldOptionDefault = (field, fieldOptions) => {
  const option = fieldOptions[field];
  return notBlank(option) ? option.defaultValue : null;
};
const setDefaults = (appState, fieldData) => {
  const options = fieldOptions(appState);
  const audiences = getAudiences(appState);
  const audienceDefault = getFieldOptionDefault("audience", options);
  const subProjects = getSubProjects(appState);
  const subProjectDefault = getFieldOptionDefault("sub_project", options);
  const ratings = getVideoRatings(appState);
  const ratingDefault = getFieldOptionDefault("rating", options);
  const languages = applicationReducer.getProjectLanguages(appState);
  const languageDefault = getFieldOptionDefault("language", options);

  const defaults = {
    title: getFieldOptionDefault("title", options),
    subtitle: getFieldOptionDefault("subtitle", options),
    purchaseUrl: getFieldOptionDefault("purchase_url", options),
    publisher: getFieldOptionDefault("publisher_name", options),
    vendor: getFieldOptionDefault("pod_vendor", options),
    volume: getFieldOptionDefault("volume", options),
    edition: getFieldOptionDefault("edition", options),
    isbn: getFieldOptionDefault("isbn", options),
    publicationYear: getFieldOptionDefault("publication_year", options),
    description: getFieldOptionDefault("description", options),
    keywords: getFieldOptionDefault("keywords", options),
    language: languageDefault ?? getDefaultLanguage(languages),
    series: getFieldOptionDefault("series", options),
    episode: getFieldOptionDefault("episode", options),
    customFreeform: getFieldOptionDefault("custom_freeform", options),
    customFreeform2: getFieldOptionDefault("custom_freeform_2", options),
    audience: notBlank(audienceDefault)
      ? find(propEq("name", audienceDefault), audiences)
      : null,
    subProject: notBlank(subProjectDefault)
      ? find(propEq("subProjectId", subProjectDefault), subProjects)
      : null,
    rating: notBlank(ratingDefault)
      ? find(propEq("name", ratingDefault), ratings)
      : null,
  };

  return mergeRight(fieldData, pickBy(notNil, defaults));
};

const localStorageLoaded = (
  data,
  intro,
  basicDetails,
  applicationState,
  state,
) =>
  pipe(
    setLoadingLocalStorage(false),
    setFields(
      isEmpty(data)
        ? setDefaults(applicationState, initialState.fields)
        : data.fields,
    ),
    setContributors(
      isEmpty(data) ? initialState.contributors : data.contributors,
    ),
    setCompleted(isEmpty(data) ? initialState.completed : data.completed),
  )(state);

const primaryCategoryChanged = (category, applicationState, state) =>
  pipe(setField(["primaryCategory"], category), setCompleted(false))(state);

const secondaryCategoryChanged = (category, applicationState, state) =>
  pipe(setField(["secondaryCategory"], category), setCompleted(false))(state);

const tertiaryCategoryChanged = (category, applicationState, state) =>
  pipe(setField(["tertiaryCategory"], category), setCompleted(false))(state);

const cancelSubmission = (applicationState, state) =>
  pipe(
    setFields(setDefaults(applicationState, initialState.fields)),
    setErrors(initialState.errors),
    setContributors(initialState.contributors),
    setCompleted(false),
  )(state);

const clearFileDetails = (applicationState, state) => {
  return pipe(
    setFields(setDefaults(applicationState, initialState.fields)),
    setErrors(initialState.errors),
    setCompleted(false),
  )(state);
};

const collectFlags = appState => {
  // collectPublicationYear is a legacy method of requiring a date to be saved
  // the new way is using field options,
  // but keeping this for now
  return {
    collectPublicationYear:
      applicationReducer.getCollectPublicationYear(appState),
  };
};

const fieldOptions = appState => {
  const fieldOptions = applicationReducer.getFieldOptions(appState);
  //TODO: Remove this when all fields automatically get field options.

  if (!fieldOptions["audience"]) {
    fieldOptions["audience"] = {
      required: !!applicationReducer.getAudiences(appState).length,
    };
  }

  return fieldOptions;
};

const fileDetailsReducer = (
  state = initialState,
  action,
  submissionState,
  applicationState,
  introState,
  basicDetailsState,
) => {
  switch (action.type) {
    case fileDetailsActions.COMPLETE_STEP:
    case submissionActions.SUBMIT:
      return completeStep(
        validateDetails(
          submissionReducer.getWorkType(submissionState),
          getContributors(state),
          collectFlags(applicationState),
          fieldOptions(applicationState),
        ),
        state,
      );

    case fileDetailsActions.AUDIENCE_CHANGED:
      return audienceChanged(action.audience, applicationState, state);

    case fileDetailsActions.SUB_PROJECT_CHANGED:
      return subProjectChanged(action.subProject, applicationState, state);

    case fileDetailsActions.CUSTOM_DROPDOWN_CHANGED:
      return customDropdownChanged(
        action.customDropdownItem,
        applicationState,
        state,
      );

    case fileDetailsActions.CONTRIBUTOR_DIALOG_FIELD_BLURRED:
      return validateContributorDialogField(
        action.field,
        action.value,
        fieldOptions(applicationState),
        state,
      );

    case fileDetailsActions.CONTRIBUTOR_DIALOG_FIELD_CHANGED:
      return setContributorDialogField(action.path, action.value, state);

    case fileDetailsActions.CONTRIBUTOR_DIALOG_ROLE_CHANGED:
      return contributorDialogRoleChanged(action.role, state);

    case fileDetailsActions.CONTRIBUTOR_ROLES_LOADED:
      return setContributorRoles(action.contributorRoles, state);

    case fileDetailsActions.FIELD_BLURRED:
      return fieldBlurred(
        submissionReducer.getWorkType(submissionState),
        collectFlags(applicationState),
        action.field,
        action.value,
        fieldOptions(applicationState),
        state,
      );

    case fileDetailsActions.FIELD_CHANGED:
      return fieldChanged(action.path, action.value, state);

    case fileDetailsActions.HIDE_CONTRIBUTOR_DIALOG:
      return hideContributorDialog(state);

    case fileDetailsActions.HIDE_REMOVE_CONTRIBUTOR_DIALOG:
      return hideRemoveContributorDialog(state);

    case fileDetailsActions.HIDE_REMOVE_TRACK_DIALOG:
      return hideRemoveTrackDialog(state);

    case fileDetailsActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(
        action.data,
        introState,
        basicDetailsState,
        applicationState,
        state,
      );

    case fileDetailsActions.REMOVE_CONTRIBUTOR:
      return pipe(
        removeContributor(action.index),
        hideRemoveContributorDialog,
      )(state);

    case fileDetailsActions.SAVE_ADD_CONTRIBUTOR:
      return saveNewContributor(fieldOptions(applicationState))(state);

    case fileDetailsActions.SAVE_EDIT_CONTRIBUTOR:
      return saveEditContributor(fieldOptions(applicationState))(state);

    case fileDetailsActions.SHOW_ADD_CONTRIBUTOR_DIALOG:
      return showAddContributorDialog(state);

    case fileDetailsActions.SHOW_EDIT_CONTRIBUTOR_DIALOG:
      return showEditContributorDialog(action.index, state);

    case fileDetailsActions.SHOW_REMOVE_CONTRIBUTOR_DIALOG:
      return showRemoveContributorDialog(action.index, state);

    case fileDetailsActions.SHOW_REMOVE_TRACK_DIALOG:
      return showRemoveTrackDialog(action.track, state);

    case fileDetailsActions.VIDEO_RATING_CHANGED:
      return videoRatingChanged(action.rating, state);

    case fileDetailsActions.VIDEO_RATINGS_LOADED:
      return setVideoRatings(action.videoRatings, state);

    case fileDetailsActions.LICENSE_TYPE_CHANGED:
      return licenseTypeChanged(action.licenseType, state);

    case fileDetailsActions.LICENSE_TYPES_LOADED:
      return setLicenseTypes(action.licenseTypes, state);

    case fileDetailsActions.AGE_RANGE_CHANGED:
      return ageRangeChanged(action.ageRange, state);

    case fileDetailsActions.AGE_RANGES_LOADED:
      return setAgeRanges(action.ageRanges, state);

    case fileDetailsActions.PRIMARY_CATEGORY_CHANGED:
      return primaryCategoryChanged(action.category, applicationState, state);

    case fileDetailsActions.SECONDARY_CATEGORY_CHANGED:
      return secondaryCategoryChanged(action.category, applicationState, state);

    case fileDetailsActions.TERTIARY_CATEGORY_CHANGED:
      return tertiaryCategoryChanged(action.category, applicationState, state);

    case submissionActions.ADD_ANOTHER_WORK:
    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(applicationState, state);

    case submissionActions.SET_WORK_TYPE_ID:
      return clearFileDetails(applicationState, state);

    case basicDetailsActions.COMPLETE_STEP:
      return contributorFromBasicDetails(
        fieldOptions(applicationState),
        introState,
        basicDetailsState,
        state,
      );

    case submissionActions.SET_ACTIVE_STEP:
      return action.step === steps.FILE_DETAILS
        ? setCompleted(false)(state)
        : state;

    default:
      return state;
  }
};

export default fileDetailsReducer;
